import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useApiCall } from '../hooks/useApiCall';
import { User, type BackendUser } from '../types';
import { authLogger } from '../utils/logger';
import { isMobileUserAgent } from '../utils/mobileUtils';
import { getServiceHeaders } from '../utils/serviceUtils';
import { sessionGet } from '../utils/storage';
import { API_BASE } from '../utils/api';

interface EmailAuthProps {
  onSuccess?: () => void;
  onCancel?: () => void;
  /** Переход на вход через Яндекс ID — второй способ «по почте». */
  onSelectYandex?: () => void;
}

/** Длина кода из письма (бэкенд шлёт 6 цифр, ведущий ноль значим — держим строкой). */
const CODE_LENGTH = 6;
/** Пауза перед повторной отправкой: бэкенд лимитирует запросы (ошибка 1013). */
const RESEND_COOLDOWN_SEC = 60;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const errorMessage = (e: unknown, fallback: string): string =>
  (e && typeof e === 'object' && 'message' in e && typeof (e as Error).message === 'string'
    ? (e as Error).message
    : null) || fallback;

/** utm из адреса SSO — на входе по почте происходит и регистрация, источник терять нельзя. */
const utmQuery = (): string => {
  const qs = new URLSearchParams(window.location.search);
  const params = new URLSearchParams();
  const utm = qs.get('utm') || qs.get('utm_string');
  if (utm) params.set('utm', utm);
  qs.forEach((value, key) => {
    if (key.startsWith('utm_') && value) params.set(key, value);
  });
  const query = params.toString();
  return query ? `?${query}` : '';
};

function backendUserToEmailUser(backend: BackendUser): User {
  return {
    id: backend.email || backend.uid || 'unknown',
    name: backend.name && backend.surname
      ? `${backend.name} ${backend.surname}`
      : backend.name || backend.email || 'Почта',
    avatar: backend.photo || '',
    platform: 'email',
  };
}

const EmailAuth: React.FC<EmailAuthProps> = ({ onSuccess, onCancel, onSelectYandex }) => {
  const { login, setError, sendToParent, redirectToReturnUrl } = useAuth();
  const { execute } = useApiCall();

  // method — выбор способа входа «по почте»: Яндекс ID или код на любой адрес.
  const [step, setStep] = useState<'method' | 'email' | 'code'>(onSelectYandex ? 'method' : 'email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [errorText, setErrorText] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(0);
  const [codeFocused, setCodeFocused] = useState(false);
  const codeInputRef = useRef<HTMLInputElement>(null);
  /** Код, который уже уходил на проверку сам — чтобы автоотправка не зациклилась на неверном. */
  const autoSubmittedRef = useRef<string | null>(null);

  // Обратный отсчёт до повторной отправки кода.
  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setTimeout(() => setCooldown((value) => value - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  useEffect(() => {
    if (step === 'code') codeInputRef.current?.focus();
  }, [step]);

  const sendCode = useCallback(
    async (resend = false) => {
      const normalized = email.trim().toLowerCase();
      if (!EMAIL_RE.test(normalized)) {
        setErrorText('Введите корректный адрес почты');
        return;
      }
      try {
        setBusy(true);
        setErrorText(null);
        setError(null);

        await execute({
          url: `${API_BASE}/oauth/email/send-code${utmQuery()}`,
          method: 'POST',
          headers: { ...getServiceHeaders() },
          data: { email: normalized },
        });

        authLogger.success('Код отправлен на почту', { resend });
        setEmail(normalized);
        setStep('code');
        setNotice(resend ? 'Отправили новый код' : `Код отправлен на ${normalized}`);
        setCooldown(RESEND_COOLDOWN_SEC);
      } catch (e: unknown) {
        const message = errorMessage(e, 'Не удалось отправить код');
        authLogger.error('Почта: код не отправлен', { error: message });
        setErrorText(message);
      } finally {
        setBusy(false);
      }
    },
    [email, execute, setError],
  );

  const verifyCode = useCallback(async () => {
    if (code.length !== CODE_LENGTH) {
      setErrorText(`Код состоит из ${CODE_LENGTH} цифр`);
      return;
    }

    try {
      setBusy(true);
      setErrorText(null);
      setError(null);

      const result = await execute({
        url: `${API_BASE}/oauth/email/verify-code${utmQuery()}`,
        method: 'POST',
        headers: { ...getServiceHeaders() },
        data: { email, code },
      });

      if (!result?.token) {
        throw new Error('Бэкенд не вернул токен по коду из письма');
      }

      const backendUser = (await execute({
        url: `${API_BASE}/user`,
        method: 'GET',
        headers: {
          'ssmm-token': result.token,
          ...getServiceHeaders(),
        },
      })) as BackendUser | null;

      if (!backendUser) {
        throw new Error('Не удалось загрузить профиль пользователя');
      }

      authLogger.success('Почта: вход выполнен');
      login(backendUserToEmailUser(backendUser), result.token, backendUser);

      const urlParams = new URLSearchParams(window.location.search);
      const returnUrl = urlParams.get('return_url') || sessionGet('ssmm-return-url');

      if (returnUrl && isMobileUserAgent()) {
        redirectToReturnUrl(result.token);
        return;
      }

      onSuccess?.();
    } catch (e: unknown) {
      const message = errorMessage(e, 'Не удалось подтвердить код');
      authLogger.error('Почта: код не подошёл', { error: message });
      setErrorText(message);
      setNotice(null);
      sendToParent({ type: 'AUTH_ERROR', error: message });
    } finally {
      setBusy(false);
    }
  }, [code, email, execute, login, onSuccess, redirectToReturnUrl, sendToParent, setError]);

  // Код набран полностью — проверяем сам, без лишнего тапа по кнопке.
  useEffect(() => {
    if (step !== 'code' || busy) return;
    if (code.length !== CODE_LENGTH) return;
    if (autoSubmittedRef.current === code) return;
    autoSubmittedRef.current = code;
    void verifyCode();
  }, [busy, code, step, verifyCode]);

  const handleBack = () => {
    if (step === 'code') {
      setStep('email');
      setCode('');
      setNotice(null);
      setErrorText(null);
      autoSubmittedRef.current = null;
      return;
    }
    if (step === 'email' && onSelectYandex) {
      setStep('method');
      setErrorText(null);
      return;
    }
    onCancel?.();
  };

  return (
    <div className="flex flex-col min-h-screen bg-zinc-900">
      {/* Header */}
      <div className="px-6 pt-8 pb-6 border-b border-zinc-800/50">
        <button
          onClick={handleBack}
          className="flex items-center mb-6 transition-colors text-zinc-500 hover:text-zinc-300"
          type="button"
        >
          <svg className="mr-1 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          <span className="text-sm">Назад</span>
        </button>

        <div className="flex items-center mb-2 space-x-3">
          <div className="w-10 h-10 rounded-xl bg-[#8B5CF6] flex items-center justify-center">
            <svg className="w-5 h-5 text-on-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
          <div>
            <h1 className="text-lg font-semibold text-white">Почта</h1>
            <p className="text-xs text-zinc-500">
              {step === 'method'
                ? 'Выберите способ входа'
                : step === 'email'
                  ? 'Вход по коду из письма'
                  : 'Введите код из письма'}
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 px-6 py-6">
        {errorText && (
          <div className="p-3 mb-4 rounded-xl border bg-red-500/10 border-red-500/30">
            <p className="text-sm text-red-400">{errorText}</p>
          </div>
        )}

        {notice && !errorText && (
          <div className="p-3 mb-4 rounded-xl border bg-emerald-500/10 border-emerald-500/30">
            <p className="text-sm break-words text-emerald-400">{notice}</p>
          </div>
        )}

        {step === 'method' ? (
          <div className="space-y-3">
            <button
              type="button"
              onClick={() => onSelectYandex?.()}
              className="w-full flex items-center px-4 py-3.5 rounded-xl bg-[#FC3F1D]/10 border border-[#FC3F1D]/30 hover:bg-[#FC3F1D]/20 transition-all duration-200"
            >
              <div className="w-9 h-9 rounded-lg bg-[#FC3F1D] flex items-center justify-center flex-shrink-0">
                <span className="text-base font-bold leading-none text-on-accent">Я</span>
              </div>
              <div className="flex-1 ml-3 min-w-0 text-left">
                <div className="text-sm font-medium text-white">Яндекс ID</div>
                <div className="text-xs text-zinc-500">Вход аккаунтом Яндекса</div>
              </div>
              <svg className="ml-auto w-4 h-4 text-zinc-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>

            <button
              type="button"
              onClick={() => setStep('email')}
              className="w-full flex items-center px-4 py-3.5 rounded-xl bg-[#8B5CF6]/10 border border-[#8B5CF6]/30 hover:bg-[#8B5CF6]/20 transition-all duration-200"
            >
              <div className="w-9 h-9 rounded-lg bg-[#8B5CF6] flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-on-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <div className="flex-1 ml-3 min-w-0 text-left">
                <div className="text-sm font-medium text-white">Другая почта</div>
                <div className="text-xs text-zinc-500">Пришлём код на ваш адрес</div>
              </div>
              <svg className="ml-auto w-4 h-4 text-zinc-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        ) : step === 'email' ? (
          <div className="space-y-3">
            <input
              type="email"
              autoComplete="email"
              inputMode="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && !busy && void sendCode()}
              placeholder="you@example.com"
              aria-label="Адрес электронной почты"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              enterKeyHint="send"
              /* text-base = 16px: иначе Safari на айфоне зумит страницу при фокусе */
              className="w-full px-3 py-2.5 rounded-lg bg-zinc-800 border border-zinc-700 text-white text-base placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-[#8B5CF6]/40"
            />
            <button
              type="button"
              disabled={busy}
              onClick={() => void sendCode()}
              className="w-full py-2.5 rounded-lg bg-[#8B5CF6] text-on-accent text-sm font-medium hover:bg-[#7c4deb] disabled:opacity-50 disabled:hover:bg-[#8B5CF6] transition-colors"
            >
              {busy ? 'Отправляем…' : 'Получить код'}
            </button>
            <p className="text-xs leading-relaxed text-center text-zinc-500">
              Пришлём одноразовый код на эту почту. Если аккаунта ещё нет — он создастся при первом входе.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {/* Одно настоящее поле поверх шести ячеек: так работают вставка из буфера,
                автоподстановка кода и цифровая клавиатура, а выглядит как обычный OTP-ввод.
                type=text + inputMode=numeric + pattern — комбинация, на которой iOS
                открывает именно цифровую раскладку (у type=number ещё и стрелки в десктопе). */}
            <div className="relative">
              <input
                ref={codeInputRef}
                type="text"
                autoComplete="one-time-code"
                inputMode="numeric"
                pattern="[0-9]*"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
                enterKeyHint="done"
                /* maxLength намеренно нет: он режет вставку ДО очистки, и из «068 387» дошло бы 5 цифр */
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, CODE_LENGTH))}
                onFocus={() => setCodeFocused(true)}
                onBlur={() => setCodeFocused(false)}
                onKeyDown={(e) => e.key === 'Enter' && !busy && void verifyCode()}
                aria-label={`Код из письма, ${CODE_LENGTH} цифр`}
                /* text-base = 16px: на меньшем шрифте Safari на айфоне зумит страницу при фокусе */
                className="absolute inset-0 z-10 w-full h-full text-base text-transparent bg-transparent border-0 opacity-0 appearance-none focus:outline-none"
              />
              <div className="flex gap-2 justify-between" aria-hidden="true">
                {Array.from({ length: CODE_LENGTH }).map((_, index) => {
                  const char = code[index] ?? '';
                  const isCaret = codeFocused && index === Math.min(code.length, CODE_LENGTH - 1);
                  return (
                    <div
                      key={index}
                      className={`flex flex-1 justify-center items-center h-12 rounded-lg border text-lg font-medium tabular-nums transition-colors ${
                        isCaret
                          ? 'bg-zinc-800 border-[#8B5CF6] text-white'
                          : char
                            ? 'bg-zinc-800 border-zinc-700 text-white'
                            : 'bg-zinc-800/60 border-zinc-800 text-zinc-600'
                      } ${busy ? 'opacity-60' : ''}`}
                    >
                      {char || (isCaret ? <span className="w-px h-5 animate-pulse bg-[#8B5CF6]" /> : '')}
                    </div>
                  );
                })}
              </div>
            </div>
            <button
              type="button"
              disabled={busy}
              onClick={() => void verifyCode()}
              className="w-full py-2.5 rounded-lg bg-[#8B5CF6] text-on-accent text-sm font-medium hover:bg-[#7c4deb] disabled:opacity-50 disabled:hover:bg-[#8B5CF6] transition-colors"
            >
              {busy ? 'Проверяем…' : 'Войти'}
            </button>

            <div className="flex justify-between items-center pt-1">
              <button
                type="button"
                onClick={handleBack}
                className="text-xs transition-colors text-zinc-500 hover:text-zinc-300"
              >
                Изменить почту
              </button>
              <button
                type="button"
                disabled={busy || cooldown > 0}
                onClick={() => void sendCode(true)}
                className="text-xs transition-colors text-zinc-500 hover:text-zinc-300 disabled:hover:text-zinc-500 disabled:opacity-60"
              >
                {cooldown > 0 ? `Выслать повторно через ${cooldown} с` : 'Выслать код повторно'}
              </button>
            </div>

            <p className="text-xs leading-relaxed text-center text-zinc-500">
              Код можно вставить целиком. Письмо не пришло? Проверьте папку «Спам» — оно приходит в течение минуты.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailAuth;
